// Relative on purpose: the server (Meta CAPI) imports this file too.
import { parseBgPhone } from './phone'

export const REDACTED = '[redacted]'

const EMAIL_RE = /[^\s@/?&=]+@[^\s@/?&=]+\.[^\s@/?&=]+/
const PHONE_PREFIX_RE = /^(\+|00|0|359)/

/** Click ids and campaign ids are long digit or alphanumeric strings, never personal data. */
const SAFE_URL_PARAMS = new Set([
  'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'srsltid', '_gl',
  'utm_id', 'gad_campaignid', 'gad_source',
])

export function looksLikeEmail(value: string): boolean {
  return EMAIL_RE.test(value)
}

export function looksLikePhone(value: string): boolean {
  const compact = value.replace(/[\s\-().]/g, '')
  return /^\+?\d{7,15}$/.test(compact) && PHONE_PREFIX_RE.test(compact)
}

export function looksLikePii(value: string): boolean {
  return looksLikeEmail(value) || looksLikePhone(value)
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '))
  }
  catch {
    return value
  }
}

/**
 * Replaces every URL query or fragment parameter that looks like an email or a phone
 * number with `[redacted]`, e.g. `/search?q=ivan@abv.bg`. Returns '' for an empty input
 * and the input unchanged when it is not a URL.
 */
export function sanitizeUrl(url: string | null | undefined, base = 'https://vodi4ka.com'): string {
  if (!url) return ''
  let parsed: URL
  try {
    parsed = new URL(url, base)
  }
  catch {
    return looksLikePii(url) ? REDACTED : url
  }

  for (const [key, value] of [...parsed.searchParams.entries()]) {
    if (SAFE_URL_PARAMS.has(key.toLowerCase())) continue
    if (looksLikePii(value)) parsed.searchParams.set(key, REDACTED)
  }

  if (parsed.hash && looksLikePii(safeDecode(parsed.hash.slice(1)))) parsed.hash = ''

  if (looksLikePii(safeDecode(parsed.pathname))) {
    parsed.pathname = parsed.pathname
      .split('/')
      .map(segment => (looksLikePii(safeDecode(segment)) ? REDACTED : segment))
      .join('/')
  }

  return parsed.toString()
}

/** Search terms are reported as typed (trimmed), unless they look like personal data. */
export function sanitizeSearchTerm(raw: unknown): string {
  const term = String(raw ?? '').trim()
  return looksLikePii(term) ? REDACTED : term
}

export function normalizeEmailForHash(email: string | null | undefined): string | null {
  const value = String(email ?? '').trim().toLowerCase().replace(/\s+/g, '')
  return value && value.includes('@') ? value : null
}

/** E.164 (`+359…`) for Google; Meta wants the same digits without the plus. */
export function normalizePhoneE164(phone: string | null | undefined): string | null {
  const raw = String(phone ?? '').trim()
  if (!raw) return null
  const parsed = parseBgPhone(raw)
  return parsed?.isValid() ? parsed.number : null
}

export async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('')
}

export interface HashedUserData {
  sha256_email?: string
  sha256_phone_google?: string
  sha256_phone_meta?: string
}

export async function hashUserData(input: { email?: string | null; phone?: string | null }): Promise<HashedUserData> {
  const out: HashedUserData = {}
  const email = normalizeEmailForHash(input.email)
  const phone = normalizePhoneE164(input.phone)
  if (email) out.sha256_email = await sha256Hex(email)
  if (phone) {
    out.sha256_phone_google = await sha256Hex(phone)
    out.sha256_phone_meta = await sha256Hex(phone.replace(/^\+/, ''))
  }
  return out
}
